import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Homepage default titles emitted by RegionalSEO on "/". Prerender must NOT
// accept a snapshot whose <title> matches one of these for any non-home route.
const HOMEPAGE_DEFAULT_TITLES = new Set<string>([
  "Warehouse Automation Company in the USA | MTLI Group",
  "Warehouse Automation Company | MTLI Group",
]);

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    // Prerender readiness signal, puppeteer waits on this before snapshotting.
    // Reset immediately on navigation, then flip true once the route's real
    // <main><h1> has rendered AND the document.title is not the homepage
    // default (unless we are actually on "/").
    if (typeof window !== "undefined") {
      (window as unknown as { __PRERENDER_READY?: boolean }).__PRERENDER_READY = false;
    }

    const isHome = pathname === "/" || pathname === "/en" || pathname === "/en/" || pathname === "/fr" || pathname === "/fr/";

    let cancelled = false;
    let tries = 0;
    const maxTries = 900; // ~15s at 16ms

    const check = () => {
      if (cancelled) return;
      const w = window as unknown as { __PRERENDER_READY?: boolean; __PRERENDER_PENDING?: number };
      const h1 = document.querySelector("main h1");
      const title = (document.title || "").trim();
      const titleOk = title.length > 0;
      const titleNotHomeDefault = isHome || !HOMEPAGE_DEFAULT_TITLES.has(title);
      const h1Text = (h1?.textContent || "").trim();
      const pending = w.__PRERENDER_PENDING ?? 0;

      if (h1 && h1Text.length > 0 && titleOk && titleNotHomeDefault && pending === 0) {
        // Give React one more paint for any post-mount head/schema updates.
        requestAnimationFrame(() => {
          if (!cancelled) {
            w.__PRERENDER_READY = true;
          }
        });
        return;
      }
      if (tries++ > maxTries) {
        // Give up, let puppeteer's own validation reject if still wrong.
        w.__PRERENDER_READY = true;
        return;
      }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
};

export default ScrollToTop;
